import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { Alert, AlertTitle } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { debounce } from 'debounce';
import * as yaml from 'js-yaml';
import moment from 'moment';
import React, { useCallback, useContext, useState } from 'react';
import UserContext from '../../auth/UserContext';
import AwsRegions from '../../common/AwsRegions';
import LoadingButton from '../../common/LoadingButton';
import FileUtils from '../../families/tests/FileUtils';
import MobxContext from '../../mobx/MobxContext';
import FaroParameterListInput from './FaroParameterListInput';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    panel: {
      padding: 16,
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
    },
    section: {
      marginBottom: 32
    },
    sectionHeader: {
      marginBottom: 8,
      display: 'flex',
      justifyContent: 'space-between'
    },
    actionButtonsContainer: {
      display: 'flex',
      alignItems: 'start'
    },
    header: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'space-between'
      }
    },
    titleContainer: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        marginBottom: 0
      }
    },
    primaryTitleContainer: {
      display: 'flex'
    },
    cancelButton: {
      marginRight: 16
    },
    alert: {
      marginBottom: 24
    },
    disabledText: {
      color: theme.palette.text.secondary
    },
    hiddenFileInput: {
      display: "none"
    },
    searchTextField: {
      marginRight: 24
    },
    parameterActionsContainer: {
      display: 'flex'
    },
    parameterTableToolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 16
    },
    kmsSwitch: {
      minWidth: 200,
      marginRight: 0
    },
    dialogTitle: {
      paddingBottom: 0
    },
    roleAutocomplete: {
      margin: '24px 0 16px 0'
    }
  })
);

function getParametersFromFile(fileJson) {
  // Two file types are supported
  // 
  // CLI file type:
  // key1: val1
  // key2: val2
  // ------------------
  // Normal file type
  // key1:
  //   Value: val1
  //   kms_key: optionalkey
  // key2:
  //   Value: val2

  if (typeof fileJson !== 'object') {
    throw new Error('Invalid parameter file: missing parameter names.');
  }

  const keys = Object.keys(fileJson);
  if (!keys.length) {
    throw new Error('Invalid parameter file: file is empty.');
  }

  return keys.reduce((accumulator, key) => {
    const param = { Name: String(key), Value: '' };
    const valueType = typeof fileJson[key];

    if (valueType === 'string' || valueType === 'boolean' || valueType === 'number') {
      param.Value = String(fileJson[key]);
    } else if (valueType === 'object' && fileJson[key].hasOwnProperty('Value')) {
      param.Value = String(fileJson[key].Value);
      if (fileJson[key].kms_key) {
        param['KmsKeyArn'] = fileJson[key].kms_key;
      }
    } else {
      throw new Error(`Invalid parameter in file: ${JSON.stringify(fileJson[key])}`);
    }

    accumulator.push(param);
    return accumulator;
  }, []);
}

interface Props {
  paramSet: any;
  assumeRoleArn: string;
  title: string;
  environments: any[];
  disableEditingDetails?: boolean;
  onParamSetChange: (paramSet: any) => void;
  onSave: () => Promise<any>;
  onSaveComplete: (paramSet: any) => void;
  onCancel: () => void;
  onAssumeRoleArnChange?: (roleArn: string) => void;
}

function FaroParamSetForm(props: Props) {
  const {
    paramSet,
    assumeRoleArn,
    title,
    environments,
    disableEditingDetails,
    onAssumeRoleArnChange,
    onParamSetChange,
    onSave,
    onSaveComplete,
    onCancel
  } = props;
  const classes = useStyles({});
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [userRoles, setUserRoles] = useState(null);
  const [search, setSearch] = useState(null);
  const [lastFileLoadTime, setLastFileLoadTime] = useState('');
  const [enableKmsKeyColumn, setEnableKmsKeyColumn] = useState(paramSet.parameters.some(param => !!param.KmsKeyArn));
  const { environmentStore } = useContext(MobxContext);
  const user = useContext(UserContext);

  const debouncedOnParamSetChange = debounce(onParamSetChange, 200);
  const debouncedOnSearchChange = debounce(setSearch, 200);

  const onTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedOnParamSetChange({ ...paramSet, name: event.target.value });
  };

  const onEnvironmentChange = (event: React.ChangeEvent<{}>, value: any) => {
    onParamSetChange({ ...paramSet, accountId: value ? value.AccountID : '' });
  };

  const onRegionChange = (event: React.ChangeEvent<{}>, value: any) => {
    onParamSetChange({ ...paramSet, region: value ? value.code : '' });
  };

  const onParametersChange = useCallback((newParameters) => {
    onParamSetChange(prevParamSet => ({ ...prevParamSet, parameters: newParameters }));
  }, [onParamSetChange]);

  const onUserRoleSelectionDialogClose = () => {
    if (onAssumeRoleArnChange) {
      onAssumeRoleArnChange(null);
    }
    setUserRoles(null);
  };

  const onSelectedRoleChange = (event: React.ChangeEvent<{}>, value: any) => {
    onAssumeRoleArnChange(value);
  };

  const onEnableKmsKeyColumnToggled = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnableKmsKeyColumn(event.target.checked);
  };

  const onAssumeRoleArnSelected = () => {
    if (assumeRoleArn) {
      // Save param set
      onSaveWrapper();
    } else {
      setShowErrors(true);
    }
  };

  const onSearchInputKeyPress = (event) => {
    // Only handle enter key type
    if (event.which === 13) {
      // Clear scheduled execution
      debouncedOnSearchChange.clear();
      setSearch(event.target.value);
    }
  }

  const onFileSelected = (file) => {
    if (file) {
      setValidationError(null);
      new FileUtils().readFileAsString(file).then(fileStr => {
        const fileJson = yaml.safeLoad(fileStr);

        const newParameters = getParametersFromFile(fileJson);
        onParametersChange(newParameters);
        setLastFileLoadTime(`${moment().valueOf()}`);
      }).catch(error => {
        setValidationError([error.message]);
        setLastFileLoadTime(`${moment().valueOf()}`);
      });
    }
  };

  const onSaveWrapper = () => {
    setShowErrors(false);

    if (!paramSet.name || !paramSet.accountId || !paramSet.region) {
      setShowErrors(true);
    } else {
      if (!assumeRoleArn) {
        // Get roles associated with User and in the selected account
        const roles = Array.from(new Set(user.Roles.filter(role => role.split(':')[4] === paramSet.accountId)));
        setUserRoles(roles);
      } else {
        setSaveInProgress(true);
        onSave().then((savedParmSet) => {
          setSaveInProgress(false);
          onSaveComplete(savedParmSet);
        }).catch(error => {
          onUserRoleSelectionDialogClose();
          setSaveInProgress(false);
          if (error.data.errorMessage) {
            setValidationError(Array.isArray(error.data.errorMessage) ? error.data.errorMessage : [error.data.errorMessage]);
          } else {
            setValidationError(['An unexpected error occurred, please try your request again'])
          }
        });
      }
    }
  };

  const environment = environmentStore.getEnvironmentByAccountId(paramSet.accountId);

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <div className={classes.primaryTitleContainer}>
            <Typography variant="h6" gutterBottom>{title}</Typography>
          </div>
        </div>
        <div>
          <Button color="primary" variant="outlined" onClick={onCancel} className={classes.cancelButton}>Cancel</Button>
          <LoadingButton
            color="primary"
            loading={saveInProgress && !userRoles}
            variant="contained"
            onClick={onSaveWrapper}>
            Save
          </LoadingButton>
        </div>
      </div>
      {validationError && <Alert severity="warning" className={classes.alert}>
        <AlertTitle>Failed to save parameter set</AlertTitle>
        {validationError.map((error, index) => <Typography key={index}>{error}</Typography>)}
      </Alert>}
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6' gutterBottom>Parameter Set Details</Typography>
        </div>
        <div className={classes.panel}>
          <Grid container spacing={8}>
            <Grid item xs={12} md={4}>
              <TextField
                required
                name='Name'
                label='Name'
                error={showErrors && !paramSet.name}
                helperText={showErrors && !paramSet.name ? 'Name is required' : ''}
                defaultValue={paramSet.name}
                onChange={onTextFieldChange}
                fullWidth
                disabled={disableEditingDetails}
                InputProps={disableEditingDetails ? {
                  classes: {
                    disabled: classes.disabledText
                  }
                } : null}
                InputLabelProps={{
                  shrink: true
                }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={disableEditingDetails}
                options={environments}
                value={environment || null}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.Alias}
                onChange={onEnvironmentChange}
                getOptionSelected={(option, value) => value ? option.EnvironmentName === value.EnvironmentName : false}
                renderOption={(option, { selected }) => (
                  <div>
                    <Typography display="block">{option.Alias}</Typography>
                    <Typography variant="caption" display="block">{option.AccountID}</Typography>
                  </div>
                )}
                renderInput={(params) => <TextField
                  {...params}
                  label='Environment'
                  error={showErrors && !paramSet.accountId}
                  helperText={showErrors && !paramSet.accountId ? 'Environment is required' : ''}
                  InputProps={disableEditingDetails ? {
                    classes: {
                      disabled: classes.disabledText
                    }
                  } : params.InputProps}
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={disableEditingDetails}
                options={AwsRegions}
                value={paramSet.region || null}
                onChange={onRegionChange}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.code}
                getOptionSelected={(option, value) => value ? option.code === value : false}
                renderInput={(params) => <TextField
                  {...params}
                  label='Region'
                  error={showErrors && !paramSet.region}
                  helperText={showErrors && !paramSet.region ? 'Region is required' : ''}
                  InputProps={disableEditingDetails ? {
                    classes: {
                      disabled: classes.disabledText
                    }
                  } : params.InputProps}
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography color='textSecondary' variant='h6'>Parameters</Typography>
          <div className={classes.parameterActionsContainer}>
            <TextField
              size="small"
              defaultValue={search}
              className={classes.searchTextField}
              autoComplete="off"
              margin="none"
              variant="outlined"
              placeholder="Filter by name or value"
              inputProps={{ 'aria-label': 'bare', spellCheck: false }}
              onKeyPress={onSearchInputKeyPress}
              onChange={event => debouncedOnSearchChange(event.target.value)}
            />
            <Button key={lastFileLoadTime} variant="outlined" size="small" component="label">
              Load from file
                <input
                type="file"
                accept="text/yaml,text/x-yaml,application/x-yaml"
                className={classes.hiddenFileInput}
                onChange={(event) => onFileSelected(event.target.files[0])}
              />
            </Button>
          </div>
        </div>
        <div className={classes.panel}>
          <div className={classes.parameterTableToolbar}>
            <Typography color='textSecondary'>{paramSet.parameters.length} parameter{paramSet.parameters.length !== 1 ? 's' : ''}</Typography>
            <FormControlLabel
              className={classes.kmsSwitch}
              control={
                <Switch
                  size="small"
                  checked={enableKmsKeyColumn}
                  onChange={onEnableKmsKeyColumnToggled}
                  name="enableKmsKeyColumn"
                  color="primary"
                />
              }
              label="Show KMS Keys"
            />
          </div>
          <FaroParameterListInput
            key={lastFileLoadTime} // Key is used for force re-mount on new file load
            hideKmsKeyArn={!enableKmsKeyColumn}
            search={search}
            defaultParameters={paramSet.parameters}
            onParametersChange={onParametersChange}
          />
        </div>
      </div>
      {userRoles && <Dialog
        onClose={onUserRoleSelectionDialogClose}
        aria-labelledby="param-set-role-dialog"
        open
        disableBackdropClick
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="param-set-role-dialog" className={classes.dialogTitle}>Select an IAM role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is the role that Faro will associate the parameter set with.
          </DialogContentText>
          <Autocomplete
            className={classes.roleAutocomplete}
            options={userRoles}
            value={assumeRoleArn}
            getOptionLabel={(option) => option}
            onChange={onSelectedRoleChange}
            renderInput={(params) => <TextField
              {...params}
              label='Role'
              error={showErrors && !assumeRoleArn}
              helperText={showErrors && !assumeRoleArn ? 'Role is required' : ''}
              InputLabelProps={{
                shrink: true
              }} />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onUserRoleSelectionDialogClose} color="primary">
            Cancel
          </Button>
          <LoadingButton
            color="primary"
            loading={saveInProgress}
            variant="contained"
            onClick={onAssumeRoleArnSelected}>
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>}
    </div>
  );
};

export default FaroParamSetForm;
